import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Key, UserMinus, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export const ManageTokensPage = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unassigning, setUnassigning] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [resetPinDialog, setResetPinDialog] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [resettingPin, setResettingPin] = useState(false);

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    setLoading(true);
    try {
      // Get session and username from localStorage (set during login)
      const session = localStorage.getItem('session');
      const username = localStorage.getItem('username');
      
      if (!session || !username) {
        toast.error('Session not found. Please login again.');
        setLoading(false);
        return;
      }

      // Call backend API which proxies to Mi-Token API
      const response = await axios.post(
        `${API_BASE_URL}/api/tokens/list`,
        {
          username: username,
          session: session
        }
      );

      if (response.data.result === 'success') {
        // Parse the tokens from the response
        const tokenList = response.data.details || [];
        // Map the token data to match expected format
        const mappedTokens = tokenList.map(token => ({
          id: token.tokenName,
          serial: token.tokenSerial
        }));
        setTokens(mappedTokens);
      } else if (response.data.result === 'failure' && response.data.details && response.data.details.includes('no tokens')) {
        setTokens([]);
      } else {
        console.error('Token fetch error:', response.data);
        setTokens([]);
      }
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
      toast.error('Failed to load tokens');
      setTokens([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignClick = (token) => {
    setSelectedToken(token);
    setConfirmDialog(true);
  };

  const handleResetPinClick = (token) => {
    setSelectedToken(token);
    setResetPinDialog(true);
    setNewPin('');
    setConfirmNewPin('');
  };

  const handleResetPinConfirm = async () => {
    if (!selectedToken) return;

    // Validate PIN
    if (!newPin || newPin.length < 4) {
      toast.error('PIN must be at least 4 digits');
      return;
    }

    if (newPin !== confirmNewPin) {
      toast.error('PINs do not match');
      return;
    }

    setResettingPin(true);
    try {
      const session = localStorage.getItem('session');
      
      if (!session) {
        toast.error('Session not found. Please login again.');
        setResettingPin(false);
        return;
      }

      // Call backend API to reset PIN
      const response = await axios.post(
        `${API_BASE_URL}/api/tokens/reset-pin`,
        {
          token: selectedToken.serial,
          new_pin: newPin,
          session: session
        }
      );

      if (response.data.result === 'success') {
        toast.success(response.data.details || 'PIN reset successfully');
        setResetPinDialog(false);
        setSelectedToken(null);
        setNewPin('');
        setConfirmNewPin('');
      } else if (response.data.result === 'unauthorized') {
        toast.error('Session expired. Please login again.');
        setResetPinDialog(false);
      } else {
        toast.error(response.data.details || 'Failed to reset PIN');
      }
    } catch (error) {
      console.error('Failed to reset PIN:', error);
      toast.error('Failed to reset PIN. Please try again.');
    } finally {
      setResettingPin(false);
    }
  };

  const handleUnassignConfirm = async () => {
    if (!selectedToken) return;

    setUnassigning(true);
    try {
      const session = localStorage.getItem('session');
      
      if (!session) {
        toast.error('Session not found. Please login again.');
        setUnassigning(false);
        return;
      }

      // Call backend API which proxies to Mi-Token API
      const response = await axios.post(
        `${API_BASE_URL}/api/tokens/unassign`,
        {
          token: selectedToken.serial,
          session: session
        }
      );

      if (response.data.result === 'success') {
        toast.success(response.data.details || 'Token unassigned successfully');
        setConfirmDialog(false);
        setSelectedToken(null);
        // Reload tokens
        await fetchTokens();
      } else if (response.data.result === 'unauthorized') {
        toast.error('Session expired. Please login again.');
        setConfirmDialog(false);
      } else {
        toast.error(response.data.details || 'Failed to unassign token');
      }
    } catch (error) {
      console.error('Failed to unassign token:', error);
      toast.error('Failed to unassign token. Please try again.');
    } finally {
      setUnassigning(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-slate-900 dark:text-white">
            Manage Tokens
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2 font-ibm">
            View and manage your authentication tokens
          </p>
        </div>
        {/* Total Tokens Counter */}
        <Card className="p-4 min-w-[200px]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-950 rounded-lg flex items-center justify-center">
              <Key className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-ibm text-muted-foreground">Total Tokens</p>
              <p className="text-2xl font-outfit font-bold text-foreground" data-testid="total-tokens-count">
                {loading ? '--' : tokens.length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-outfit">My Tokens</CardTitle>
          <CardDescription className="font-ibm">
            Your assigned authentication tokens
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
            </div>
          ) : tokens.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-900 dark:text-white font-ibm font-semibold text-lg mb-2">
                No Tokens Assigned
              </p>
              <p className="text-slate-500 dark:text-slate-400 font-ibm">
                You currently have no authentication tokens assigned to your account.
              </p>
            </div>
          ) : (
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-ibm font-semibold w-20">#</TableHead>
                    <TableHead className="font-ibm font-semibold">ID</TableHead>
                    <TableHead className="font-ibm font-semibold">Serial</TableHead>
                    <TableHead className="font-ibm font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tokens.map((token, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-ibm font-medium">{index + 1}</TableCell>
                      <TableCell className="font-ibm">{token.id || 'N/A'}</TableCell>
                      <TableCell className="font-ibm font-mono text-sm">{token.serial}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="font-ibm"
                            onClick={() => handleResetPinClick(token)}
                          >
                            <Key className="h-4 w-4 mr-2" />
                            Reset PIN
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="font-ibm"
                            onClick={() => handleUnassignClick(token)}
                          >
                            <UserMinus className="h-4 w-4 mr-2" />
                            Unassign
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Unassign Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit">Confirm Unassign Token</DialogTitle>
            <DialogDescription className="font-ibm">
              Are you sure you want to unassign this token? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedToken && (
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400 font-ibm">Serial:</span>
                    <span className="font-mono font-medium">{selectedToken.serial}</span>
                  </div>
                  {selectedToken.id && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 font-ibm">ID:</span>
                      <span className="font-medium">{selectedToken.id}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setConfirmDialog(false);
                  setSelectedToken(null);
                }}
                disabled={unassigning}
                className="font-ibm"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleUnassignConfirm}
                disabled={unassigning}
                className="font-ibm"
              >
                {unassigning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Unassigning...
                  </>
                ) : (
                  'Unassign Token'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset PIN Dialog */}
      <Dialog open={resetPinDialog} onOpenChange={setResetPinDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit">Reset Token PIN</DialogTitle>
            <DialogDescription className="font-ibm">
              Enter a new PIN for this token (minimum 4 digits)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedToken && (
              <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-400 font-ibm">Serial:</span>
                    <span className="font-mono font-medium">{selectedToken.serial}</span>
                  </div>
                  {selectedToken.id && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400 font-ibm">ID:</span>
                      <span className="font-medium">{selectedToken.id}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium font-ibm mb-2">New PIN</label>
                <input
                  type="password"
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md font-ibm"
                  placeholder="Enter new PIN"
                  disabled={resettingPin}
                />
              </div>
              <div>
                <label className="block text-sm font-medium font-ibm mb-2">Confirm PIN</label>
                <input
                  type="password"
                  value={confirmNewPin}
                  onChange={(e) => setConfirmNewPin(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md font-ibm"
                  placeholder="Confirm new PIN"
                  disabled={resettingPin}
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setResetPinDialog(false);
                  setSelectedToken(null);
                  setNewPin('');
                  setConfirmNewPin('');
                }}
                disabled={resettingPin}
                className="font-ibm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPinConfirm}
                disabled={resettingPin}
                className="font-ibm"
              >
                {resettingPin ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  'Reset PIN'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
